import React, { useEffect, useState, useCallback } from "react";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatIDR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy } from "lucide-react";

const empty = { name: "", sku: "", size: "", color: "", category: "", subcategory: "", brand: "", vendor: "", price: 0, is_pro: false, online_visible: true };

export default function BrandProducts() {
  const { staff } = useAuth();
  const canCreate = hasPermission(staff, "brand.products.create");
  const canEdit = hasPermission(staff, "brand.products.edit");
  const canDelete = hasPermission(staff, "brand.products.delete");
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [cloneOpen, setCloneOpen] = useState(null); // product being cloned
  const [cloneSku, setCloneSku] = useState("");

  const load = useCallback(async () => {
    try { const r = await staffApi.get("/products", { params: q ? { search: q } : {} }); setRows(r.data); }
    catch (err) { toast.error(formatApiError(err)); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...empty, ...p }); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (editing) await staffApi.put(`/products/${editing.id}`, payload);
      else await staffApi.post("/products", payload);
      toast.success("Product saved");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try { await staffApi.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const doClone = async () => {
    if (!cloneSku.trim()) return toast.error("New SKU required");
    try { await staffApi.post(`/products/${cloneOpen.id}/clone`, { sku: cloneSku.trim() }); toast.success("Cloned"); setCloneOpen(null); setCloneSku(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <StaffShell context="brand">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="tracking-overline text-xs text-muted-foreground">BRAND CATALOG</p>
          <h1 className="text-3xl font-semibold mt-1" data-testid="products-title">Products</h1>
          <p className="text-sm text-muted-foreground">Products cannot be deleted while any service still references them as a consumable.</p>
        </div>
        {canCreate && (
          <Button onClick={openNew} data-testid="new-product-btn" className="gap-1"><Plus className="w-4 h-4" /> New product</Button>
        )}
      </div>

      <Card className="rounded-lg mb-4"><CardContent className="p-4 flex gap-2">
        <Input placeholder="Search name or SKU" value={q} onChange={(e) => setQ(e.target.value)} data-testid="prod-search-input" />
        <Button variant="outline" onClick={load} data-testid="prod-search-btn">Search</Button>
      </CardContent></Card>

      <Card className="rounded-lg">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>SKU</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>PRO</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p) => (
                <TableRow key={p.id} data-testid={`prod-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell className="text-xs">{p.size}</TableCell>
                  <TableCell className="text-xs">{p.sku}</TableCell>
                  <TableCell>{p.category}</TableCell>
                  <TableCell className="text-xs">{p.brand}</TableCell>
                  <TableCell>{formatIDR(p.price)}</TableCell>
                  <TableCell>{p.is_pro && <Badge className="bg-accent text-accent-foreground rounded-full text-[10px]">PRO</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex items-center gap-0.5">
                      {canEdit && <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-prod-${p.id}`}><Pencil className="w-4 h-4" /></Button>}
                      {canCreate && <Button size="icon" variant="ghost" onClick={() => { setCloneOpen(p); setCloneSku(`${p.sku}-COPY`); }} data-testid={`clone-prod-${p.id}`}><Copy className="w-4 h-4" /></Button>}
                      {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(p)} data-testid={`delete-prod-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                      {!canEdit && !canCreate && !canDelete && <span className="text-muted-foreground italic text-xs">Read-only</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="prod-modal">
          <DialogHeader><DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="prod-sku" disabled={!!editing} /></div>
            <div><Label>Size</Label><Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} /></div>
            <div><Label>Color</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div><Label>Subcategory</Label><Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} /></div>
            <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
            <div><Label>Vendor</Label><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} /></div>
            <div><Label>Price (IDR)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" /></div>
            <div className="flex items-center gap-3 mt-6">
              <Switch checked={form.is_pro} onCheckedChange={(v) => setForm({ ...form, is_pro: v })} data-testid="prod-pro" /> <Label>PRO (professional-use only)</Label>
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="prod-save-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cloneOpen} onOpenChange={(v) => !v && setCloneOpen(null)}>
        <DialogContent data-testid="clone-prod-modal">
          <DialogHeader><DialogTitle>Clone product</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Cloning <strong>{cloneOpen?.name}</strong>. Give the copy a new unique SKU.</p>
          <div><Label>New SKU</Label><Input value={cloneSku} onChange={(e) => setCloneSku(e.target.value)} data-testid="clone-prod-sku" /></div>
          <DialogFooter><Button variant="ghost" onClick={() => setCloneOpen(null)}>Cancel</Button><Button onClick={doClone} data-testid="confirm-clone-prod">Clone</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
